import React, { useState, useRef, FC } from 'react';
import ReactCrop, { Crop, centerCrop, makeAspectCrop, convertToPixelCrop } from 'react-image-crop';
import { Modal, Button } from '@marriott/mi-ui-library';
import { StyledImageCropper } from './ImageCropper.styles';
import { ImageCropperProps } from './ImageCropper.types';
import 'react-image-crop/dist/ReactCrop.css';

export const ImageCropper: FC<ImageCropperProps> = ({ show, image, onClose, onConfirm, label }) => {
  const imgRef = useRef<HTMLImageElement>(null);

  const [cropAspect, setCropAspect] = useState<Crop>();
  const [selectedCropAspect, setSelectedCropAspect] = useState<Crop | null>(null);
  const aspect = 3 / 2;

  const centerAspectCrop = (mediaWidth: number, mediaHeight: number, aspect: number) => {
    return centerCrop(
      makeAspectCrop({ unit: '%', width: 90 }, aspect, mediaWidth, mediaHeight),
      mediaWidth,
      mediaHeight
    );
  };

  const onImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    if (aspect) {
      const { width, height } = event.currentTarget;
      const defaultCrop = centerAspectCrop(width, height, aspect);
      setCropAspect(defaultCrop);
      setSelectedCropAspect(defaultCrop);
    }
  };

  const getCroppedImage = async (): Promise<File | null> => {
    if (!imgRef.current || !selectedCropAspect) return null;

    const canvas = document.createElement('canvas');
    const pixelCrop = convertToPixelCrop(selectedCropAspect, imgRef.current.width, imgRef.current.height);

    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = pixelCrop.width * scaleX;
    canvas.height = pixelCrop.height * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(
      imgRef.current,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      pixelCrop.width * scaleX,
      pixelCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return new Promise<File | null>(resolve => {
      canvas.toBlob(
        blob => {
          if (!blob) return resolve(null);
          resolve(new File([blob], `cropped_${Date.now()}.jpg`, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        0.8
      );
    });
  };

  const handleConfirm = async () => {
    if (imgRef.current && selectedCropAspect) {
      const croppedFile = await getCroppedImage();
      if (croppedFile) {
        onConfirm(croppedFile);
      }
    }
    onClose();
  };

  return (
    <StyledImageCropper data-testid="groups-ImageCropper" data-component-name="m-groups-ImageCropper">
      <Modal
        show={show}
        popupOpenState={show}
        setPopupOpenState={onClose}
        onClose={onClose}
        handleBlur={true}
        disableScrollOnBody={true}
      >
        <Modal.Header popupHeaderOnCLoseFunc={onClose} labelText={label.heading} />
        <Modal.Body className="main-section">
          <ReactCrop
            crop={cropAspect}
            onChange={crop => setCropAspect(crop)}
            onComplete={c => setSelectedCropAspect(c)}
            aspect={aspect}
          >
            <img ref={imgRef} src={image.link} onLoad={onImageLoad} alt={image?.name} crossOrigin="anonymous" />
          </ReactCrop>
        </Modal.Body>
        <Modal.FooterGeneric
          actions={
            <>
              <Button
                className="m-button-m m-button-secondary"
                buttonCopy={label.cancel}
                ariaLabel={label.cancelAriaLabel}
                callback={onClose}
              />

              <Button
                className="m-button-m m-button-primary"
                buttonCopy={label.confirm}
                ariaLabel={label.confirmAriaLabel}
                callback={handleConfirm}
                isDisabled={!selectedCropAspect || (selectedCropAspect?.height == 0 && selectedCropAspect?.width == 0)}
              />
            </>
          }
        />
      </Modal>
    </StyledImageCropper>
  );
};
